import { Card, LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  CardContent,
  CircularProgress,
  List,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import {
  CountryRuleEnum,
  RetentionRuleType,
  useGetFallbackRetentionRulesQuery,
  useGetRetentionRulesQuery,
} from '../../../../graphql/generated';
import { ExpandableCard } from '../../molecules/ExpandableCard';
import { LensButton } from '../../molecules/LensButton';
import { RuleCardContent } from '../../molecules/RuleCardContent';
import { SectionHeader } from '../../molecules/SectionHeader';
import { AddRuleDrawer } from '../../organisms/AddRuleDrawer';
import { RuleCardVariant, RuleDrawerVariant } from '../../utils';

export interface RuleDrawer {
  isOpen: boolean;
  variant: RuleDrawerVariant;
  rule: RetentionRuleType | null;
  country?: string;
}

export const RetentionRules = () => {
  const [expandedCountries, setExpandedCountries] = useState<
    Record<string, boolean>
  >({});

  const [ruleDrawer, setRuleDrawer] = useState<RuleDrawer>({
    isOpen: false,
    variant: RuleDrawerVariant.NEW,
    rule: null,
  });

  const { data } = useGetRetentionRulesQuery();
  const retentionRules = data?.getRetentionRules ?? [];

  const {
    data: fallbackData,
    isLoading: fallbackLoading,
    isError: fallbackError,
  } = useGetFallbackRetentionRulesQuery();
  const fallbackRules = fallbackData?.getFallbackRetentionRules ?? [];

  const toggleCountry = (country: string) => {
    setExpandedCountries((prev) => ({
      ...prev,
      [country]: !prev[country],
    }));
  };

  const handleCloseDrawer = () =>
    setRuleDrawer((prev) => ({ ...prev, isOpen: false }));

  const { tokens } = useTheme<LensTheme>();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.core.spacing[3].value,
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: tokens.core.spacing[1].value,
          padding: tokens.core.spacing[0].value,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: tokens.semantic.fontWeight.emphasis.value,
          }}
        >
          Retention Rules
        </Typography>
        <Stack direction="row" spacing={2} useFlexGap>
          <LensButton
            variant="secondary"
            onClick={() =>
              setRuleDrawer({
                rule: null,
                isOpen: true,
                variant: RuleDrawerVariant.FALLBACK,
              })
            }
          >
            Add fallback rule
          </LensButton>
          <LensButton
            variant="primary"
            onClick={() =>
              setRuleDrawer({
                rule: null,
                isOpen: true,
                variant: RuleDrawerVariant.NEW,
              })
            }
          >
            Add new rule
          </LensButton>
        </Stack>
      </Stack>
      <AddRuleDrawer
        variant={ruleDrawer.variant}
        isOpen={ruleDrawer.isOpen}
        onClose={handleCloseDrawer}
        rule={ruleDrawer.rule}
        country={ruleDrawer.country}
      />
      <SectionHeader
        title="Fallback rules"
        subtitle="Applied when no country specific rule matches"
      />

      <Card
        sx={{
          padding: tokens.core.spacing[3].value,
          border: `1px solid ${tokens.semantic.color.action.secondary.disabledOutline.value}`,
          borderRadius: tokens.semantic.radius.lg.value,
          boxShadow: 'none !important',
        }}
      >
        <CardContent
          sx={{
            padding: '0 !important',
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.core.spacing[2].value,
          }}
        >
          {fallbackLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : fallbackError ? (
            <Typography variant="body1" color="error">
              Failed to load fallback rules. Please try again.
            </Typography>
          ) : fallbackRules.length === 0 ? (
            <Typography variant="body1">
              No fallback rules configured
            </Typography>
          ) : (
            fallbackRules.map((rule) => (
              <RuleCardContent
                variant={RuleCardVariant.FALLBACK}
                setRuleDrawer={setRuleDrawer}
                key={rule.id}
                rule={rule}
                title="Fallback rule"
                subtitle={`Created ${dayjs(rule.createdAt).format('M/D/YYYY')}`}
              />
            ))
          )}
        </CardContent>
      </Card>

      <SectionHeader title="Retention rules" />
      <List
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.core.spacing[2].value,
          margin: 0,
          padding: 0,
          listStyle: 'none',
        }}
      >
        {retentionRules.map(({ country, rules }) => {
          const isExpanded = expandedCountries[country];
          const ruleCount = rules.length;
          return (
            <ExpandableCard
              key={country}
              title={country}
              toggleKey={country}
              items={rules}
              isExpanded={isExpanded}
              onToggle={toggleCountry}
              chipLabel={`${ruleCount} ${ruleCount === 1 ? 'rule' : 'rules'}`}
              renderExpandedContent={(rules) => (
                <>
                  {rules.map((rule) => (
                    <RuleCardContent
                      variant={RuleCardVariant.RETENTION}
                      setRuleDrawer={setRuleDrawer}
                      title={
                        rule.countryRule === CountryRuleEnum.Case
                          ? 'Case country'
                          : 'Document rule'
                      }
                      subtitle={`Updated ${dayjs(rule.updatedAt).format(
                        'M/D/YYYY',
                      )}`}
                      key={rule.id}
                      rule={rule}
                      country={country}
                      padding={`0 0 0 ${tokens.core.spacing[5].value}`}
                    />
                  ))}
                </>
              )}
            />
          );
        })}
      </List>
    </Box>
  );
};
